import { CheckCircle2, TriangleAlert } from 'lucide-react'
import { useState } from 'react'

import type { ReportTrialBalanceLine } from '@/api/reports-types'
import { ErrorSurface } from '@/components/error-surface'
import { MoneyDisplay } from '@/components/money-display'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'

import { today } from './constants'
import { BranchFilterField } from './filters'
import { REPORTS_PERMISSIONS, useReportsPermissions } from './permissions'
import { useReportTrialBalance } from './queries'
import { ReportExportButtons } from './report-export-buttons'
import { ReportHeaderNotice } from './report-header-notice'
import { RestrictedPanel } from './restricted-panel'

/**
 * The reporting module's own trial balance — the same figures Ledger → Trial balance already
 * screens, read through this module's own gate instead: a single-branch-or-institution position as
 * at a business date, with a header block stating what it is as of and which of its sources
 * answered. Simpler than the ledger's own screen on purpose — it carries no opening balance, no
 * movement-versus-position split, and no Bank of Ghana roll-up, because it is not the accountant's
 * working screen; it is the same figures read by whoever holds `reports.trialbalance.view` without
 * the ledger module's own, wider permission.
 */
export function ReportTrialBalanceSection() {
  const permissions = useReportsPermissions()

  const [asOf, setAsOf] = useState(today)
  const [branchId, setBranchId] = useState<string | null>(null)

  const query = useReportTrialBalance({ asOf, branchId }, permissions.canViewTrialBalance && asOf !== '')

  if (!permissions.canViewTrialBalance) {
    return <RestrictedPanel what="The trial balance" permission={REPORTS_PERMISSIONS.trialBalanceView} />
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="tb-as-of">As of</Label>
            <Input id="tb-as-of" type="date" value={asOf} onChange={(event) => setAsOf(event.target.value)} />
          </div>
          <BranchFilterField id="tb-branch" branchId={branchId} onChange={setBranchId} />
        </CardContent>
      </Card>

      {query.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} title="The trial balance could not be read" />
      ) : (
        <>
          <ReportHeaderNotice header={query.data.header} />
          <ReportExportButtons query={{ report: 'TrialBalance', asOf, branchId }} />

          <Alert tone={query.data.isBalanced ? 'success' : 'danger'}>
            {query.data.isBalanced ? <CheckCircle2 aria-hidden /> : <TriangleAlert aria-hidden />}
            <div className="min-w-0 space-y-1">
              <AlertTitle>
                {query.data.isBalanced ? 'This trial balance balances' : 'This trial balance does not balance'}
              </AlertTitle>
              <AlertDescription>
                {query.data.isBalanced
                  ? 'Total debits equal total credits over every postable account.'
                  : 'Total debits and total credits disagree — find it before anything downstream is reported.'}
              </AlertDescription>
            </div>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Accounts</CardTitle>
              <Badge tone="neutral">{query.data.rows.length} rows</Badge>
            </CardHeader>
            <CardContent className="p-0">
              {query.data.rows.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-body">No account carries a balance for this scope.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <tr>
                        <TableHead scope="col">Account</TableHead>
                        <TableHead scope="col">Name</TableHead>
                        <TableHead scope="col">Type</TableHead>
                        <TableHead scope="col" className="text-right">Debit</TableHead>
                        <TableHead scope="col" className="text-right">Credit</TableHead>
                      </tr>
                    </TableHeader>
                    <TableBody>
                      {query.data.rows.map((row) => (
                        <TrialBalanceRow key={row.glAccountCode} row={row} />
                      ))}
                      <TableRow className="bg-surface-muted hover:bg-surface-muted">
                        <TableCell colSpan={3} className="text-xs font-semibold text-heading">
                          Total
                        </TableCell>
                        <TableCell className="text-right">
                          <MoneyDisplay
                            value={query.data.totalDebit.minor}
                            currency={query.data.totalDebit.currency}
                            symbol={false}
                            emphasis="strong"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <MoneyDisplay
                            value={query.data.totalCredit.minor}
                            currency={query.data.totalCredit.currency}
                            symbol={false}
                            emphasis="strong"
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}

function TrialBalanceRow({ row }: { row: ReportTrialBalanceLine }) {
  return (
    <TableRow className={row.isPostable ? undefined : 'bg-surface-muted/60'}>
      <TableCell className="font-mono text-xs whitespace-nowrap" style={{ paddingLeft: `${row.level * 12 + 16}px` }}>
        {row.glAccountCode}
      </TableCell>
      <TableCell className={row.isPostable ? 'text-sm' : 'text-sm font-semibold text-heading'}>
        {row.accountName}
      </TableCell>
      <TableCell className="text-xs whitespace-nowrap text-muted">{row.accountType}</TableCell>
      <TableCell className="text-right">
        <MoneyDisplay value={row.debit.minor} currency={row.debit.currency} symbol={false} />
      </TableCell>
      <TableCell className="text-right">
        <MoneyDisplay value={row.credit.minor} currency={row.credit.currency} symbol={false} />
      </TableCell>
    </TableRow>
  )
}
